import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserForm } from "../../../components/users/UserForm";
import { useAuth } from "@/contexts/AuthContext";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useUsers } from "@/hooks/users/useUsers";
import { apiClient } from "@/services/core/apiClient";

const AddUser = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();
  const { refetch } = useUsers();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: {
    firstname?: string;
    lastname?: string;
    email?: string;
    phonenumber?: string;
    password?: string;
    usertype?: string;
    scheduleConfig?: {
      fromDate: string;
      toDate: string;
      timezone: string;
      daySlots: Record<string, { enabled: boolean; slots: { fromTime: string; toTime: string }[] }>;
    };
  }) => {
    if (!user?.id || !token) {
      handleError(new Error("Authentication required"));
      return;
    }

    setIsLoading(true);
    
    try {
      // Map user type values: CS->CS, PES->PE, VPE->ES
      const userTypeMapping: Record<string, string> = {
        CS: "CS",
        PES: "PE",
        VPE: "ES",
      };

      // Day name to numeric mapping (Monday=1, Sunday=7)
      // NOTE: Normalize keys to avoid casing mismatches (e.g. "MONDAY" vs "Monday").
      const dayNameToNumber: Record<string, number> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };

      const getDayOfWeekNumber = (dayName: string): number | undefined => {
        const key = (dayName || "").trim().toLowerCase();
        return dayNameToNumber[key];
      };

      // Convert 12-hour time format to 24-hour format (HH:MM:SS)
      const convertTo24Hour = (time12h: string): string => {
        if (!time12h) return "00:00:00";
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':');
        let hoursNum = parseInt(hours, 10);
        
        if (modifier === 'PM' && hoursNum !== 12) {
          hoursNum += 12;
        } else if (modifier === 'AM' && hoursNum === 12) {
          hoursNum = 0;
        }
        
        return `${hoursNum.toString().padStart(2, '0')}:${minutes}:00`;
      };

      // Build dayTimes array from schedule config
      const dayTimes: Array<{
        availabilityDayTimeId: null;
        dayOfWeek: number;
        availableStartTime: string;
        availableEndTime: string;
      }> = [];

      if (data.scheduleConfig?.daySlots) {
        Object.entries(data.scheduleConfig.daySlots).forEach(([dayName, dayData]) => {
          if (dayData.enabled && dayData.slots.length > 0) {
            dayData.slots.forEach(slot => {
              if (slot.fromTime && slot.toTime) {
                const dayOfWeek = getDayOfWeekNumber(dayName);
                if (!dayOfWeek) return;
                dayTimes.push({
                  availabilityDayTimeId: null,
                  dayOfWeek,
                  availableStartTime: convertTo24Hour(slot.fromTime),
                  availableEndTime: convertTo24Hour(slot.toTime),
                });
              }
            });
          }
        });
      }

      // Build request body with dto
      const requestBody: Record<string, any> = {
        first_name: data.firstname,
        last_name: data.lastname,
        email: data.email,
        password: data.password,
        user_type: userTypeMapping[data.usertype] || data.usertype,
        phone_number: data.phonenumber,
        is_active: true,
        dto: {
          resourceAvailabilityId: null,
          userId: null,
          availableFromDate: data.scheduleConfig?.fromDate || null,
          availableToDate: data.scheduleConfig?.toDate || null,
          userTimezone: data.scheduleConfig?.timezone || "US/Pacific",
          dayTimes: dayTimes,
        },
      };

      const response = await apiClient.post(
        `/api/ngpep/${user.id}/ed`,
        requestBody,
        token
      );

      if (response.success) {
        handleSuccess(
          `User ${data.firstname} ${data.lastname} has been created successfully.`,
          "User Created"
        );
        // Refetch users to update the table
        refetch();
        navigate("/admin/users");
      } else {
        const errorMessage = (response.data as any)?.message || response.error || "Failed to create user";
        handleError(new Error(errorMessage));
      }
    } catch (error) {
      handleError(error, "Failed to create user");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UserForm 
      title="Add New User"
      onSubmit={handleSubmit}
      isLoading={isLoading}
    />
  );
};

export default AddUser;